import React, { Fragment, ReactNode, useState } from 'react';

import NoSSR from '../../../context/ssr/no-ssr';
import useIntersect from '../../../hooks/useIntersect';
import t from '../../../labels/labelling-utils';
import { IReportsDetailsMatch } from '../../../typings/match-reports';
import { isPreMatch } from '../../../utils/match-status';
import { slugify } from '../../../utils/slugify';
import { makeClassNames } from '../../../utils/styles';
import Cta from '../../atoms/cta/cta';
import ErrorComponent from '../../atoms/error/error';
import Spacer from '../../atoms/spacer/spacer';
import { ITeam } from '../../molecules/team/team';
import { IMenuGliderItems, MenuGliderItems } from '../../organisms';
import ReportTabs from '../../organisms/report-tabs/report-tabs';
import ErrorBoundary from '../error-boundary/error-boundary';
import Glider from '../../organisms/glider/glider';
// import './match-reports-detail.scss';

export interface IMatchReportsDetail {
  matchDayList: IReportsDetailsMatch[];
  reportsHomepageUrl?: string;
}

interface ICreateIds {
  home: string;
  away: string;
  full: string;
}

const isValidMatchList = (matchDayList: IReportsDetailsMatch[]) => Boolean(matchDayList && matchDayList.length);

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

/** TODO: WORKAROUND TO REMOVE according with BE implementations */
const remapGoalsOnPrematchStatus = (match: IReportsDetailsMatch) => {
  if (isPreMatch(match.status)) {
    match.home.goal = '-';
    match.away.goal = '-';
  }
};

const buildGliderLabel = (homeShortname: string | undefined, awayShortname: string | undefined): ReactNode => (
  <Fragment>
    {homeShortname?.toUpperCase() || ''}
    <br />
    {awayShortname?.toUpperCase() || ''}
  </Fragment>
);

const createIds = (home: ITeam, away: ITeam, fallbackKey: number): ICreateIds => {
  const idHome = slugify(/* istanbul ignore next: optional chaining */ home.seoName?.toLowerCase() || `${fallbackKey}`);
  const idAway = slugify(/* istanbul ignore next: optional chaining */ away.seoName?.toLowerCase() || `${fallbackKey}`);
  return {
    home: `${idHome}-home`,
    away: `${idAway}-away`,
    full: `${idHome}-${idAway}`,
  };
};

const getGliderItems = (matchDayList: IReportsDetailsMatch[]) =>
  matchDayList.map((match: IReportsDetailsMatch, key: number) => ({
    href: `#${createIds(match.home, match.away, key).full}`,
    label: buildGliderLabel(
      /* istanbul ignore next: optional chaining */ match?.home?.shortname,
      /* istanbul ignore next: optional chaining */ match?.away?.shortname
    ),
  }));

const classNames = () =>
  makeClassNames({
    gliderWrapper: 'match-reports-detail--wrapper',
    matchReportsDetail: 'match-reports-detail',
    content: 'match-reports-detail__content',
  });

const ssrPlaceholder = (props: IMenuGliderItems, cn: ReturnType<typeof classNames>) => {
  return (
    <div className={cn.gliderWrapper}>
      <MenuGliderItems {...props} isSSR />
    </div>
  );
};

const MatchReportsDetail = ({ matchDayList, reportsHomepageUrl }: IMatchReportsDetail) => {
  const cn = classNames();
  const gliderItems = getGliderItems(matchDayList);
  const [activeIndex, setActiveIndex] = useState(0);

  const onIntersect = (item: HTMLElement) => {
    setActiveIndex(Number(item.dataset.index));
  };

  const onSelectedGliderItem = (gliderIndex: number) => {
    if (gliderIndex !== activeIndex) {
      setActiveIndex(gliderIndex);
    }
  };

  useIntersect('[data-intersect]', onIntersect);

  const gliderComponentProps: IMenuGliderItems = {
    items: gliderItems,
    activeSlideIndex: activeIndex,
    variant: 'mgv',
    onItemSelectedCallback: onSelectedGliderItem,
  };

  return (
    <div className={cn.matchReportsDetail}>
      <ErrorBoundary hasError={!isValidMatchList(matchDayList)} errorComponent={getErrorComponent()}>
        {reportsHomepageUrl && (
          <Fragment>
            <Cta href={reportsHomepageUrl} color="blue" iconPosition="left">
              {t('match.reports.detail.back.cta')}
            </Cta>
            <Spacer size={20} />
          </Fragment>
        )}
        <NoSSR placeholder={ssrPlaceholder(gliderComponentProps, cn)}>
          <Glider
            activeIndex={activeIndex}
            items={getGliderItems(matchDayList)}
            onSelectedGliderItem={onSelectedGliderItem}
          />
        </NoSSR>
        {matchDayList.map((match: IReportsDetailsMatch, key: number) => {
          const ids = createIds(match.home, match.away, key);
          remapGoalsOnPrematchStatus(match); // TODO: workaround to remove!
          return (
            <div id={ids.full} key={ids.full} className={cn.content} data-intersect>
              <ReportTabs {...match} idHome={ids.home} idAway={ids.away} data-intersect />
            </div>
          );
        })}
      </ErrorBoundary>
    </div>
  );
};

export default MatchReportsDetail;
